import React, { useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import { getPartTypes, getPartCodes, PartTypeOption, PartCodeRecord } from '@/services/bomService';
import { usePartsByVendor } from '@/hooks/useQuotations';
import type { QuotationItemIndex } from '@/services/quotationsService';
import LoadingSpinner from '@/components/LoadingSpinner';

/**
 * Pricing info from quotation
 */
export interface QuotationPricing {
  unitPrice: number;
  leadTimeDays: number;
  quotationId: string;
}

interface PartSelectionDialogProps {
  vendorCode?: string;  // Optional: filter to show only parts quoted by this vendor
  onClose: () => void;
  onSelect: (part: PartCodeRecord, quotationPricing?: QuotationPricing) => void;
}

const PartSelectionDialog: React.FC<PartSelectionDialogProps> = ({ vendorCode, onClose, onSelect }) => {
  const [selectedTypeCode, setSelectedTypeCode] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAllParts, setShowAllParts] = useState(false);

  // Fetch parts quoted by the vendor (Firestore-based)
  const { data: vendorPartsData, isLoading: vendorPartsLoading } = usePartsByVendor(
    vendorCode && !showAllParts ? vendorCode : undefined
  );

  // Fetch part types (電子零件別) - only used when showing all parts
  const { data: partTypes, isLoading: partTypesLoading } = useQuery<PartTypeOption[]>(
    'partTypes',
    getPartTypes,
    { staleTime: 300000, enabled: showAllParts || !vendorCode }
  );

  // Fetch parts for selected type - only used when showing all parts
  const { data: parts, isLoading: partsLoading } = useQuery<PartCodeRecord[]>(
    ['partCodes', 'electronic_parts', selectedTypeCode],
    () => getPartCodes('electronic_parts', selectedTypeCode || undefined),
    {
      enabled: showAllParts || !vendorCode,
      staleTime: 60000,
    }
  );

  // Determine which mode we're in
  const isQuotationMode = !!vendorCode && !showAllParts && (vendorPartsData?.items?.length ?? 0) > 0;

  // Filter parts by search query (for all parts mode)
  const filteredParts = useMemo(() => {
    if (!parts) return [];
    if (!searchQuery) return parts;

    const query = searchQuery.toLowerCase();
    return parts.filter(
      (part) =>
        part.part_number?.toLowerCase().includes(query) ||
        part.vendor_product?.toLowerCase().includes(query) ||
        part.vendor_spec?.toLowerCase().includes(query) ||
        part.brand?.toLowerCase().includes(query)
    );
  }, [parts, searchQuery]);

  // Filter quotation items by search query
  const filteredQuotationItems = useMemo(() => {
    if (!vendorPartsData?.items) return [];
    if (!searchQuery) return vendorPartsData.items;

    const query = searchQuery.toLowerCase();
    return vendorPartsData.items.filter(
      (item) =>
        item.part_code?.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query) ||
        item.specification?.toLowerCase().includes(query) ||
        item.vendor_name?.toLowerCase().includes(query)
    );
  }, [vendorPartsData, searchQuery]);

  const handleSelectPart = (part: PartCodeRecord, pricing?: QuotationPricing) => {
    onSelect(part, pricing);
    onClose();
  };

  // Handle selecting a quotation item (convert to PartCodeRecord format)
  const handleSelectQuotationItem = (item: QuotationItemIndex) => {
    const partRecord: PartCodeRecord = {
      row_index: 0, // Not applicable for quotation items
      part_number: item.part_code,
      vendor_name: item.vendor_name,
      vendor_product: item.description,
      vendor_spec: item.specification,
      brand: item.vendor_name,
      category: '', // Not available from quotation index
      mpq: '',
    };
    const pricing: QuotationPricing = {
      unitPrice: item.best_price,
      leadTimeDays: item.best_lead_time_days,
      quotationId: item.best_quotation_id,
    };
    onSelect(partRecord, pricing);
    onClose();
  };

  // Format currency
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 2,
    }).format(price);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-5xl max-h-[85vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">選擇零件</h2>
              <p className="text-green-100 text-sm">
                {isQuotationMode ? '從廠商報價選擇品項' : '從零件庫選擇品項'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Mode Toggle (when vendor has quotations) */}
          {vendorCode && (vendorPartsData?.items?.length ?? 0) > 0 && (
            <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm text-green-800 dark:text-green-200">
                    此廠商有 <strong>{vendorPartsData?.total ?? 0}</strong> 個品項報價
                  </span>
                </div>
                <button
                  onClick={() => setShowAllParts(!showAllParts)}
                  className="text-sm text-green-600 hover:text-green-700 dark:text-green-400 underline"
                >
                  {showAllParts ? '顯示報價品項' : '顯示全部零件'}
                </button>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            {/* Part Type Dropdown - only show when in all parts mode */}
            {(!isQuotationMode) && (
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  電子零件別
                </label>
                <select
                  value={selectedTypeCode}
                  onChange={(e) => setSelectedTypeCode(e.target.value)}
                  disabled={partTypesLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:border-gray-600"
                >
                  <option value="">全部類別</option>
                  {partTypes?.map((type) => (
                    <option key={type.part_code} value={type.part_code}>
                      {type.part_code} - {type.part_type_zh} ({type.part_type_en})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Search Input */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                搜尋
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜尋品號、品名、規格..."
                  className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:border-gray-600"
                />
                <svg
                  className="absolute left-3 top-2.5 w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Parts Table - Quotation Mode */}
          {isQuotationMode && (
            <div className="overflow-x-auto max-h-[50vh] overflow-y-auto border rounded-lg">
              {vendorPartsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner />
                </div>
              ) : filteredQuotationItems.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  無符合條件的報價品項
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        雲騰品號
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        品名
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        規格
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        報價價格
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        交期(天)
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        狀態
                      </th>
                      <th className="px-4 py-3 w-20"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                    {filteredQuotationItems.map((item, index) => (
                      <tr
                        key={item.id || index}
                        className="hover:bg-green-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                        onClick={() => handleSelectQuotationItem(item)}
                      >
                        <td className="px-4 py-3 font-mono text-gray-900 dark:text-white">
                          {item.part_code}
                        </td>
                        <td className="px-4 py-3 text-gray-900 dark:text-white">{item.description}</td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-300 max-w-xs truncate" title={item.specification}>
                          {item.specification}
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-green-600 dark:text-green-400">
                          {formatPrice(item.best_price)}
                        </td>
                        <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-300">
                          {item.best_lead_time_days > 0 ? item.best_lead_time_days : '-'}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {item.has_valid_quotation ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                              有效
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                              過期
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectQuotationItem(item);
                            }}
                            className="px-3 py-1 text-sm text-white bg-green-500 rounded hover:bg-green-600 transition-colors"
                          >
                            選擇
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Parts Table - All Parts Mode */}
          {!isQuotationMode && (
            <div className="overflow-x-auto max-h-[50vh] overflow-y-auto border rounded-lg">
              {partsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner />
                </div>
              ) : filteredParts.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  {selectedTypeCode ? '此類別暫無零件資料' : '請選擇電子零件別或搜尋'}
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        雲騰品號
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        廠牌
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        廠商品名
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        規格
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        尺寸
                      </th>
                      <th className="px-4 py-3 w-20"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                    {filteredParts.map((part, index) => (
                      <tr
                        key={part.part_number || index}
                        className="hover:bg-green-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                        onClick={() => handleSelectPart(part)}
                      >
                        <td className="px-4 py-3 font-mono text-gray-900 dark:text-white">
                          {part.part_number}
                        </td>
                        <td className="px-4 py-3 text-gray-900 dark:text-white">{part.brand}</td>
                        <td className="px-4 py-3 text-gray-900 dark:text-white">{part.vendor_product}</td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{part.vendor_spec}</td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{part.mpq}</td>
                        <td className="px-4 py-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectPart(part);
                            }}
                            className="px-3 py-1 text-sm text-white bg-green-500 rounded hover:bg-green-600 transition-colors"
                          >
                            選擇
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Count */}
          <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
            顯示 {isQuotationMode ? filteredQuotationItems.length : filteredParts.length} 筆資料
            {searchQuery && ` (搜尋: "${searchQuery}")`}
            {isQuotationMode && (
              <span className="ml-2 text-green-600 dark:text-green-400">
                • 報價品項模式
              </span>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            取消
          </button>
        </div>
      </div>
    </div>
  );
};

export default PartSelectionDialog;
